import { memo, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { LockOpen } from "@material-ui/icons";
import Lock from "@material-ui/icons/Lock";
import { cookies } from "constants/settings.constants";
import Cookies from "js-cookie";
import { RootState } from "store/rootReducer";
import styled from "styled-components";
import { getStoredCustomColors } from "utils/color/getStoredCustomColors";

import { BaseMenu, Tooltip } from ".";
import ColorChooser from "./ColorChooser";
import "./ColorPicker.scss";

const default_color = "#025f9c";
const transparent_color_selected_bg = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' version='1.1' preserveAspectRatio='none' viewBox='0 0 100 100'><path d='M0 99 L99 0 L100 1 L1 100' fill='rgb(214,40,11)' /></svg>")`;

function LegendColorPicker({
  values = {
    color: default_color,
    strokeColor: default_color,
    thickness: 2,
    opacity: 100
  },
  place = "bottom",
  align = "start",
  onChange = null,
  onChangeOpacity = null,
  onChangeThickness = null,
  isAtFolderLevel = false,
  isReadOnly = false,
  includeOpacity = true,
  showGroupsNotInFilter = true,
  onClickLock,
  item,
  colorPickerAdjustableSize,
  pinnedColor,
  isScreenshotOverlayVisible = false
}) {
  const [isOpen, setOpen] = useState(false);
  const [currentValues, setValues] = useState(values);
  const [customColors, setCustomColors] = useState([]);

  const placeholderBinsEnabled = useSelector(
    (state: RootState) => state.groupBy.placeholderBinsEnabled
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  function toggle() {}

  function update(prop, value) {
    const newValues = { ...currentValues };
    newValues[prop] = value;
    setValues(newValues);

    if (onChangeOpacity && prop === "opacity") {
      onChangeOpacity(prop, value);
    } else if (onChangeThickness && prop === "thickness") {
      onChangeThickness(prop, value);
    } else {
      onChange && onChange(prop, value);
    }
  }
  useEffect(() => {
    const storedCustomColors = Cookies.get(cookies.CUSTOM_COLORS);
    if (storedCustomColors && !customColors.length)
      setCustomColors(getStoredCustomColors(storedCustomColors));
  }, [customColors]);

  // TODO: Fix infinite loop caused by this useEffect when no values are passed into the ColorPicker
  useEffect(() => {
    setValues(values);
  }, [values]);

  const styleObject = (prop, isDisabled) => {
    const styles = {
      flex: "100%",
      borderRadius: "2px",
      border: `${values.thickness}px solid ${
        prop === "strokeColor" ? values.strokeColor : "transparent"
      }`,
      backgroundColor: prop === "color" ? values.color : "white",
      boxShadow: "",
      backgroundImage: "",
      cursor: isScreenshotOverlayVisible
        ? "default"
        : isDisabled
        ? "not-allowed"
        : "pointer"
    };

    if (prop === "color" && values.color === "#FFFFFF") {
      styles.backgroundColor = "white";
      styles.border = "1px solid #D9E1E2";
    } else if (prop === "strokeColor" && values.strokeColor === "#FFFFFF") {
      styles.backgroundColor = "#D9E1E2";
      styles.border = "1px solid #D9E1E2";
      styles.boxShadow = isDisabled ? "" : `inset 0 0 0 ${values.thickness}px #FFFFFF`;
    }

    if (values[prop] === "transparent") {
      styles.backgroundImage = transparent_color_selected_bg;
      styles.border = "1px solid #D9E1E2";
    }
    return styles;
  };

  const classNames = useMemo(() => {
    function variation() {
      return `${place}-${align}`;
    }
    return `ColorPicker ${isOpen ? "isOpen" : ""} ${variation()} ${
      isAtFolderLevel ? "atFolderLevel" : ""
    } ${!isReadOnly && !isScreenshotOverlayVisible ? "hasAnimations" : ""}`;
  }, [align, isOpen, place, isReadOnly, isScreenshotOverlayVisible, isAtFolderLevel]);

  const handleClick = () => {
    if (isScreenshotOverlayVisible) return;
    if (item?.isColorLocked) {
      onClickLock?.(item);
    }
  };

  const colorRectStyle = useMemo(() => {
    const shouldDisplay =
      item?.inFilter &&
      (item?.count !== 0 || (placeholderBinsEnabled ? showGroupsNotInFilter : false)) &&
      item?.color !== null;

    return {
      width: colorPickerAdjustableSize.width,
      height: colorPickerAdjustableSize.height,
      minWidth: colorPickerAdjustableSize.width,
      minHeight: colorPickerAdjustableSize.height,
      background: shouldDisplay ? item.color : "",
      display: shouldDisplay ? "inline-block" : "none"
    };
  }, [
    colorPickerAdjustableSize,
    item?.inFilter,
    item?.count,
    item?.color,
    placeholderBinsEnabled,
    showGroupsNotInFilter
  ]);

  const trigger = (prop) => {
    const colorRectContent = (
      <span
        className={`color-rect ${
          !isScreenshotOverlayVisible && item?.isColorLocked ? "color-locked" : ""
        }`}
        style={colorRectStyle}
        onClick={!isScreenshotOverlayVisible ? handleClick : undefined}>
        {!isScreenshotOverlayVisible && (
          <span
            className="lock-icon"
            style={
              item?.color?.toUpperCase() === "#FFFFFF" ? { color: "#000000" } : undefined
            }>
            {" "}
            {item?.isColorLocked ? (
              <Lock style={{ width: colorPickerAdjustableSize.width }} />
            ) : (
              <LockOpen style={{ width: colorPickerAdjustableSize.width }} />
            )}
          </span>
        )}
      </span>
    );

    return (
      <div
        tabIndex={0}
        role="button"
        className={classNames}
        style={colorPickerAdjustableSize}
        onClick={() => !isScreenshotOverlayVisible && setOpen(!isOpen)}>
        <div
          className="flex flex-centered"
          data-testid={`select-${prop}-button`}
          style={styleObject(prop, isReadOnly)}>
          {isScreenshotOverlayVisible ? (
            colorRectContent
          ) : (
            <Tooltip title="Lock Colour" placement="bottom">
              {colorRectContent}
            </Tooltip>
          )}
        </div>
      </div>
    );
  };

  if (isScreenshotOverlayVisible) {
    return <BaseMenuWrapper>{trigger("color")}</BaseMenuWrapper>;
  }

  return (
    <BaseMenuWrapper>
      <BaseMenu
        place={place}
        align={align}
        toggle={toggle}
        trigger={trigger("color")}
        hasBlockLabel={false}>
        {() => (
          <ColorChooserWrapper className="colorChooserWrapper">
            <ColorChooser
              selectedColor={values.color}
              onChange={(color) => {
                update("color", color);
              }}
              customColors={customColors}
              setCustomColors={setCustomColors}
              includeOpacity={includeOpacity}
              selectedOpacity={values.opacity}
              onChangeOpacity={(opacity) => {
                update("opacity", opacity);
              }}
              disableTransparent={true}
              pinnedColor={pinnedColor}
            />
          </ColorChooserWrapper>
        )}
      </BaseMenu>
    </BaseMenuWrapper>
  );
}

const ColorChooserWrapper = styled.div`
  padding: 10px 16px;
`;

const BaseMenuWrapper = styled.div`
  // Center the color picker properly in legend items
  .legend-item & {
    transform: translateY(-1.5px); // Fine-tune vertical centering
  }
`;

export default memo(LegendColorPicker);
